import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { toastHelper } from '../util/toastHelper.js'

const REQUEST_URL = `${process.env.REACT_APP_REQUEST_URL}/api`
const API_VERSION = 'v1'

export const getCalendarConnections = createAsyncThunk(
	'calendar/getConnections',
	async () => {
		const res = await fetch(
			`${REQUEST_URL}/${API_VERSION}/calendar/connections`,
			{
				method: 'GET',
				credentials: 'include',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
			}
		)
		const result = await res.json()
		return result
	}
)

export const updateCalendarPreferences = createAsyncThunk(
	'calendar/updatePreferences',
	async (payload) => {
		const res = await fetch(
			`${REQUEST_URL}/${API_VERSION}/calendar/preferences`,
			{
				method: 'PATCH',
				credentials: 'include',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(payload),
			}
		)
		const result = await res.json()
		return result
	}
)

export const disconnectProvider = createAsyncThunk(
	'calendar/disconnectProvider',
	async (provider) => {
		const res = await fetch(
			`${REQUEST_URL}/${API_VERSION}/calendar/disconnect/${provider}`,
			{
				method: 'POST',
				credentials: 'include',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
			}
		)
		const result = await res.json()
		return { ...result, provider }
	}
)

export const getIcalFeedUrl = createAsyncThunk(
	'calendar/getIcalFeedUrl',
	async () => {
		const res = await fetch(
			`${REQUEST_URL}/${API_VERSION}/calendar/ical/feed-url`,
			{
				method: 'GET',
				credentials: 'include',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
			}
		)
		const result = await res.json()
		return result
	}
)

export const syncAllCalendarEvents = createAsyncThunk(
	'calendar/syncAll',
	async (payload) => {
		const res = await fetch(
			`${REQUEST_URL}/${API_VERSION}/calendar/sync-all`,
			{
				method: 'POST',
				credentials: 'include',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(payload),
			}
		)
		return await res.json()
	}
)

const getDefaultProviderPrefs = () => ({ syncEvents: true, categorySync: {} })

const initialState = {
	connections: {
		google: { connected: false },
		outlook: { connected: false },
		ical: { connected: false, feedPath: null },
	},
	preferences: {
		google: getDefaultProviderPrefs(),
		outlook: getDefaultProviderPrefs(),
		ical: getDefaultProviderPrefs(),
	},
	lastError: null,
	loading: false,
	error: null,
}

const calendarSlice = createSlice({
	name: 'calendar',
	initialState,
	reducers: {
		clearCalendarError: (state) => {
			state.lastError = null
			state.error = null
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(getCalendarConnections.pending, (state) => {
				state.loading = true
				state.error = null
			})
			.addCase(getCalendarConnections.fulfilled, (state, { payload }) => {
				state.loading = false
				if (payload?.success && payload.connections) {
					state.connections = payload.connections
					state.preferences = payload.preferences ?? state.preferences
					state.lastError = payload.lastError ?? null
				} else {
					state.error =
						payload?.message ?? 'Failed to load connections'
				}
			})
			.addCase(getCalendarConnections.rejected, (state, action) => {
				state.loading = false
				state.error =
					action.error?.message ?? 'Failed to load connections'
			})
			.addCase(updateCalendarPreferences.fulfilled, (state, action) => {
				const payload = action.payload
				if (payload?.success) {
					if (
						action.meta?.arg &&
						typeof action.meta.arg === 'object'
					) {
						const { provider, ...prefs } = action.meta.arg
						if (provider && state.preferences[provider]) {
							state.preferences[provider] = {
								...state.preferences[provider],
								...prefs,
							}
						}
					}
					toastHelper(
						payload.message ?? 'Preferences updated',
						'success'
					)
				} else {
					toastHelper(
						payload?.message ?? 'Failed to update preferences',
						'error'
					)
				}
			})
			.addCase(updateCalendarPreferences.rejected, (state, action) => {
				toastHelper(
					action.error?.message ?? 'Failed to update preferences',
					'error'
				)
			})
			.addCase(disconnectProvider.fulfilled, (state, { payload }) => {
				if (payload?.success && payload.provider) {
					if (payload.provider === 'google') {
						state.connections.google = { connected: false }
					} else if (payload.provider === 'outlook') {
						state.connections.outlook = { connected: false }
					}
					state.lastError = null
					toastHelper(payload.message ?? 'Disconnected', 'success')
				} else {
					toastHelper(
						payload?.message ?? 'Failed to disconnect',
						'error'
					)
				}
			})
			.addCase(disconnectProvider.rejected, (state, action) => {
				toastHelper(
					action.error?.message ?? 'Failed to disconnect',
					'error'
				)
			})
			.addCase(getIcalFeedUrl.fulfilled, (state, { payload }) => {
				if (payload?.success && payload.feedPath) {
					state.connections.ical = {
						...state.connections.ical,
						feedPath: payload.feedPath,
						connected: true,
					}
				}
			})
			.addCase(syncAllCalendarEvents.pending, (state) => {
				state.loading = true
				state.error = null
			})
			.addCase(syncAllCalendarEvents.fulfilled, (state, { payload }) => {
				state.loading = false
				if (payload?.success) {
					toastHelper(payload.message ?? 'Sync started', 'success')
				} else {
					toastHelper(
						payload?.message ?? 'Failed to start sync',
						'error'
					)
				}
			})
			.addCase(syncAllCalendarEvents.rejected, (state, action) => {
				state.loading = false
				toastHelper(
					action.error?.message ?? 'Failed to start sync',
					'error'
				)
			})
	},
})

export const { clearCalendarError } = calendarSlice.actions
export default calendarSlice.reducer
